import { GameEvent } from "../enums/GameEvent";
import { css, htmlArray } from "../helpers/webComponents";
import { AnnouncementRequest, GameEventService, PlaySoundEvent, SetRoomSoundEvent } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { CanvasComponent } from "./CanvasComponent";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    * {
        font-family: "Onesize";
        box-sizing: border-box;
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
    }

    .dark-overlay {
        position: fixed; /* Ensures the overlay covers the entire screen */
        top: 0;
        left: 0;
        z-index: 10;
        background-color: rgba(0, 0, 0, 0.4);
        width: 100%;
        height: 100%;
    }

    .button {
        background-color: #856343;
        outline: 1px solid #5a3f25;
        border-top: 2px solid #c78d57;
        padding: 5px 10px;

        text-transform: uppercase;
        cursor: var(--cursor-hover);
        display: inline-block;
        user-select: none;
        border-bottom: 2px solid #5a3f25;
        transition: all 0.1s ease-in-out;
        box-sizing: content-box;

        position: absolute;
        top: -35px; /* Move it above the menu */
        left: 75%;
        width: auto;
        height: 3.5%;
    }

    .button:hover {
        filter: brightness(1.1);
        transform: translateY(-3px); /* Moves the button up slightly */
        border-bottom-width: 6px;
        margin-bottom: 6px;
    }

    .button:active,
    .button:focus {
        cursor: var(--cursor-active);
        filter: brightness(0.9);
        transform: translateY(2px); /* Moves the button down slightly */
        border-bottom-width: 2px;
    }

    .menu-container {
        position: absolute;
        top: 20%;
        left: 50%;
        transform: translateX(-50%);
        width: 26rem;
    }

    .menu {
        box-sizing: border-box;
        width: 23rem;
        height: 17rem;
        position: absolute;
        background-color: #3a3738;
        background-image: url("/assets/img/ui/footer.png");
        background-repeat: no-repeat;
        background-origin: border-box;

        border-radius: 4px;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 15px;
        border-width: 18px;
        border-image: url("/assets/img/ui/room-frame.png") 160 round;
        border-style: solid;
        color: #ffad42;
    }

    .slider {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    .slider:nth-child(1) {
        margin-top: 1rem;
    }

    .slider:nth-child(2) {
        margin-top: 1rem;
        margin-bottom: 1rem;
    }

    label {
        width: fit-content;
        height: 40%;
    }

    input[type="range"] {
        -webkit-appearance: none;
        background-color: rgba(255, 255, 255, 0.7);
        width: 20rem;
        height: 2.8rem;
        border-radius: 0.1rem;
        overflow: hidden;
        cursor: var(--cursor-hover);
    }
    input[type="range"]:active {
        cursor: var(--cursor-active);
    }

    input[type="range"][step] {
        background-color: transparent;
        background-image: repeating-linear-gradient(
            to right,
            rgba(255, 255, 255, 0.2),
            rgba(255, 255, 255, 0.2) calc(5% - 1px),
            #05051a 5%
        );
    }

    input[type="range"]::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 0;
        box-shadow: -20rem 0 0 20rem rgba(255, 255, 255, 0.2);
    }

    input[type="range"]::-moz-range-thumb {
        border: none;
        width: 0;
        box-shadow: -20rem 0 0 20rem rgba(255, 255, 255, 0.2);
    }
`;

/**
 * Represents the Canvas page
 */
export class SoundComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    // private _currentGameState?: DefaultGameState;

    /** Tracks the currently playing background room sounds to prevent duplicates */
    private _currentMusic: Map<string, HTMLAudioElement> = new Map<string, HTMLAudioElement>();

    /** Cached sound effects to prevent duplicate instances */
    private _soundEffects: Map<string, HTMLAudioElement> = new Map<string, HTMLAudioElement>();

    private _volumeValues: number[] = [];

    /** Used in order to prevent
     * the NotAllowedError: play() failed because the user didn't interact with the document first. */
    private _hasInteracted: boolean = false;
    private _queuedRoomSounds: string[] = [];

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        // Initialize volumes
        this._volumeValues = [
            Number(localStorage.getItem("volume-music")) || 50, // Default 50 for music
            Number(localStorage.getItem("volume-room-sounds")) || 100, // Default 100 for room sounds
        ];

        this.detectFirstInteraction();

        // Listen for the SetRoomSound event to play room music
        this._gameEventService.addGameEventListener<SetRoomSoundEvent>(GameEvent.SetRoomSound, event => {
            this.setRoomSound(event.roomSound);
            // console.log("Playing music:", event.roomSound);
        });

        // Listen for the PlaySound event to play sound effects
        this._gameEventService.addGameEventListener<PlaySoundEvent>(GameEvent.PlaySound, event => {
            void this.playSound(event.sound, event.type);
            // console.log("Playing sound:", event.sound);
        });

        // Listen for the SoundMenuToggle event to open the volume menu
        this._gameEventService.addGameEventListener(GameEvent.SoundMenuOpen, () => {
            this.openMenu();
            // console.log("Opened menu");
        });

        // Listen for the StopAudio event to stop all audio
        this._gameEventService.addGameEventListener(GameEvent.StopAudio, () => {
            this.removeAllAudio();
        });
    }

    private detectFirstInteraction(): void {
        const setInteracted: () => void = (): void => {
            this._hasInteracted = true;
            window.removeEventListener("click", setInteracted);
            window.removeEventListener("keydown", setInteracted);

            // Resume any music that was waiting
            this.resumeBackgroundMusic();
        };

        window.addEventListener("click", setInteracted);
        window.addEventListener("keydown", setInteracted);
    }

    private resumeBackgroundMusic(): void {
        if (this._queuedRoomSounds.length > 0) {
            this.setRoomSound(this._queuedRoomSounds);
        }
    }

    /**
     * Sets background music for the current room and prevents duplicates
     * @param roomSound The array of room sounds (e.g., [ambient music, room-specific music])
     */
    private setRoomSound(roomSound: string[]): void {
        if (!this._hasInteracted) {
            // Don't try to play audio yet
            this._queuedRoomSounds = roomSound;
            return;
        }
        this._queuedRoomSounds = [];

        const currentSounds: Set<string> = new Set(this._currentMusic.keys());
        const newSoundsSet: Set<string> = new Set(roomSound);

        // Use the stored value from _volumeValues
        const musicVolume: number = this._volumeValues[0] / 100;
        const roomVolume: number = this._volumeValues[1] / 100;

        // Stop and remove any sounds that are no longer needed
        for (const sound of currentSounds) {
            if (!newSoundsSet.has(sound)) {
                // console.log(`Stopping music: ${sound}`);
                const audio: HTMLAudioElement = this._currentMusic.get(sound) as HTMLAudioElement;

                this.fadeOutAndRemove(sound, audio, 1500); // Smooth fade-out
            }
        }

        // Add and play new sounds that are not already playing
        roomSound.forEach((sound, index) => {
            if (!this._currentMusic.has(sound)) {
                // console.log(`Starting new room music: ${sound}`);
                const newSound: HTMLAudioElement = new Audio(`/assets/sound/room/${sound}`);

                // Set separate volume for music and ambience
                newSound.volume = index === 0 ? musicVolume : roomVolume;

                void newSound.play().catch((err: unknown) => {
                    if (err instanceof DOMException && err.name === "AbortError") {
                    // Ignore AbortError. It happens during cutscenes
                    }
                    else {
                        console.error(`Error playing sound: ${sound}`, err);
                    }
                });
                this._currentMusic.set(sound, newSound);

                // airport announcement is special
                if (sound === "airport-announcement.mp3" || sound === "airplane-crash.mp3") {
                    this.handleSpecialSound(sound);
                }
                else {
                    newSound.loop = true;
                }
            }
        });
    }

    /**
     * Plays a specific sound effect.
     * - Prevents duplicate instances by caching sounds.
     * @param sound The sound effect URL to play.
     */
    private async playSound(sound: string, type?: string): Promise<void> {
        if (!this._hasInteracted) {
            // Don't try to play audio yet
            return;
        }
        let correctSound: string = sound;
        if (type === "action") {
            correctSound = await this.getCorrectSound(correctSound);
        }

        const cachedSound: HTMLAudioElement | undefined = this._soundEffects.get(correctSound);
        if (cachedSound) {
            // If the sound is cached, play the existing instance
            void cachedSound.play();
            // console.log(`Playing cached sound: ${correctSound}`);
        }
        else {
            // If it's not cached, create a new sound instance using the provided URL
            // console.log(`Playing new sound effect: ${correctSound}`);
            const newSound: HTMLAudioElement = new Audio(`/assets/sound/sound-events/${correctSound}`);
            // Attempt to play the sound and handle any errors
            void newSound.play().catch((err: unknown) => {
                if (err instanceof DOMException && err.name === "NotAllowedError") {
                    // Silently ignore this one, it’s expected
                    return;
                }

                console.error(`Error playing sound: ${correctSound}`, err);
            });
            // Cache the new sound instance for future use
            this._soundEffects.set(correctSound, newSound);

            // Handle the scream in the jungle
            if (sound === "scream.mp3") {
                this.handleSpecialScreamSound(newSound, sound);
            }
        }
    }

    /**
     * Smoothly fades out and removes an audio element.
     * @param audioName The name/url of the audio to remove from the map.
     * @param audio The audio element to fade out.
     * @param duration The duration of the fade-out in milliseconds.
     */
    private fadeOutAndRemove(audioName: string, audio: HTMLAudioElement, duration: number = 1500): void {
        const fadeInterval: number = 50; // Time between each volume step (ms)
        const steps: number = duration / fadeInterval; // Calculate the number of steps to complete the fade
        const volumeStep: number = audio.volume / steps; // Calculate the amount to decrease the volume by in each step

        const fade: NodeJS.Timeout = setInterval(() => {
            // If the volume is greater than the step size, reduce the volume gradually
            if (audio.volume > volumeStep) {
                audio.volume -= volumeStep;
            }
            else {
                // If the volume is 0, stop the fade and pause the audio
                clearInterval(fade);
                audio.pause();
                audio.remove(); // Remove the audio element from the DOM
                this._currentMusic.delete(audioName); // Remove the audio from the map
            }
        }, fadeInterval); // Repeat every 50ms
    }

    /** Removes all currently playing audio elements */
    public removeAllAudio(): void {
        this._currentMusic.forEach(audio => {
            audio.pause(); // Stop playback
            audio.remove(); // Remove from DOM
        });

        this._currentMusic.clear();

        // console.info("All audio elements removed.");
    }

    /**
     * Determines the correct sound file to play.
     * If the specified sound file does not exist, falls back to a default sound.
     * @param sound The name of the sound file to check.
     * @returns The correct sound file name/url.
     */
    private async getCorrectSound(sound: string): Promise<string> {
        const soundManifestPath: string = "/assets/sound/sound-manifest.json"; // Construct the sound file path
        const defaultSound: string = "button-click.mp3"; // Fallback sound file

        try {
            const availableSounds: Set<string> | undefined = await this.loadSoundManifest(soundManifestPath);

            if (availableSounds.has(`${sound}.mp3`)) {
                return `${sound}.mp3`;
            }
            else {
                // console.info(`Sound file not listed: ${sound}, using default.`);
                return defaultSound;
            }
        }
        catch (error) {
            console.error("Uuh, this wasn't supposed to happen. Error loading sound manifest:", error);
            return defaultSound; // Ensure fallback in case of error
        }
    }

    private async loadSoundManifest(path: string): Promise<Set<string>> {
        try {
            const response: Response = await fetch(path);
            const data: { sounds: string[] } = await response.json() as { sounds: string[] };
            return new Set<string>(data.sounds);
        }
        catch (error: unknown) {
            console.error("Failed to load sound manifest:", error);
            throw new Error("Uuuh, this wasn't supposed to happen. Just ignore");
        }
    }

    /**
     * Opens the sound menu overlay and initializes its elements.
     * If the menu is already open, it simply makes it visible.
     */
    private openMenu(): void {
        if (this.shadowRoot) {
            // set the default value of music to 70, cus it's kind of loud
            const savedMusic: string | null = localStorage.getItem("volume-music");
            const defaultMusicValue: string = savedMusic !== null ? savedMusic : "70";

            const overlay: HTMLDivElement | undefined = this.shadowRoot.querySelector(".dark-overlay") as HTMLDivElement | undefined;
            if (overlay) {
            // If the menu already exists, just make it visible
                overlay.style.display = "flex";
                return;
            }
            const elements: HTMLElement[] = htmlArray`
                <style>
                    ${styles}
                </style>
                <div class="dark-overlay">
                    <div class="menu-container">
                        <a class="button">X</a>
                        <div class="menu">
                            <div class="slider">
                                <label for="music">MUSIC</label>
                                <input id="music" type="range" min="0" max="100" value="${defaultMusicValue}" step="5" />
                            </div>
                            <div class="slider">
                                <label for="room-sounds">ROOM</label>
                                <input id="room-sounds" type="range" min="0" max="100" value="100" step="5" />
                            </div>
                        </div>
                    </div>
                </div>                
            `;

            this.shadowRoot.append(...elements);

            this.shadowRoot.querySelector(".button")?.addEventListener("click", () => this.handleCloseSoundMenu());
            this.attachSliderListeners();
        }
    }

    /**
     * Handles the closing of the sound menu.
     * Plays a button click sound and hides the overlay.
     * Saves the current slider values to local storage.
     */
    private handleCloseSoundMenu(): void {
        void this.playSound("button-click.mp3");
        const overlay: HTMLDivElement = this.shadowRoot?.querySelector(".dark-overlay") as HTMLDivElement;
        overlay.style.display = "none"; // Hides instead of removing

        // Save all slider values to local storage on close
        const volumeSliders: HTMLInputElement[] = Array.from(this.shadowRoot?.querySelectorAll("input") as NodeListOf<HTMLInputElement>);
        volumeSliders.forEach(slider => {
            localStorage.setItem(`volume-${slider.id}`, slider.value);
        });
    }

    /**
     * Attaches event listeners to the volume sliders.
     * Updates the volume values and applies changes when sliders are adjusted.
     */
    private attachSliderListeners(): void {
        const volumeSliders: HTMLInputElement[] = Array.from(this.shadowRoot?.querySelectorAll("input") as NodeListOf<HTMLInputElement>);

        volumeSliders.forEach((slider: HTMLInputElement, index: number) => {
            // Load stored value from localStorage (if exists)
            const storedValue: string | null = localStorage.getItem(`volume-${slider.id}`);
            if (storedValue !== null) {
                slider.value = storedValue;
                this.setVolume(slider.id, parseFloat(storedValue));
            }

            slider.addEventListener("input", (event: Event) => {
                const target: HTMLInputElement = event.target as HTMLInputElement;
                const volume: number = parseFloat(target.value);
                this._volumeValues[index] = volume;
                // console.log(volume);
                // console.log("Using slider: " + slider.id);
                // console.log(this._volumeValues);
                this._volumeValues[index] = volume;
                this.setVolume(slider.id, volume);
            });
        });
    }

    /**
     * Sets the volume for a specific slider type (music, room sounds).
     * Normalizes the volume level and applies it to the corresponding audio elements.
     * @param slider The ID of the slider (e.g., "music", "room-sounds").
     * @param level The volume level (0-100).
     */
    private setVolume(slider: string, level: number): void {
        const normalizedLevel: number = level / 100; // Convert 0-100 to 0-1

        const currentMusicKeys: string[] = Array.from(this._currentMusic.keys());

        if (slider === "music") {
            const audio: HTMLAudioElement = this._currentMusic.get(currentMusicKeys[0]) as HTMLAudioElement;
            audio.volume = normalizedLevel;
        }
        else if (slider === "room-sounds") {
            const audio: HTMLAudioElement = this._currentMusic.get(currentMusicKeys[1]) as HTMLAudioElement;
            audio.volume = normalizedLevel;
        }
        // else if (slider === "events") {
        //     const audios: HTMLAudioElement[] = Array.from(this._soundEffects.values());
        //     audios.forEach(audio => (audio.volume = normalizedLevel));
        // }
    }

    private handleSpecialSound(sound: string): void {
        const rootComponent: CanvasComponent | null = document.querySelector("game-root");

        if (rootComponent && rootComponent.shadowRoot) {
            const gameCanvas: HTMLElement | null = rootComponent.shadowRoot.querySelector("game-canvas");
            if (gameCanvas) {
                const cachedSound: HTMLAudioElement | undefined = this._currentMusic.get(sound);

                cachedSound!.onended = async () => {
                    // send airport-announcement.mp3 to handle in the api
                    this.dispatchEvent(new CustomEvent<AnnouncementRequest>("sound-complete", {
                        detail: {
                            sound: sound,
                        },
                        bubbles: true,
                        composed: true,
                    }));
                    if (sound === "airplane-crash.mp3") {
                        await this._gameRouteService.crashAnnouncementPlayed();
                    }
                    await this._gameRouteService.endEventSound(sound);
                    // console.log("The announcement has ended.");
                    void (gameCanvas as CanvasComponent).refreshGameState();
                };
            }
        }
    }

    private handleSpecialScreamSound(newSound: HTMLAudioElement, sound: string): void {
        newSound.onended = async () => {
            await this._gameRouteService.endEventSound(sound);
            // console.log("The announcement has ended.");
        };
    }
}
